import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Fade, Zoom, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Divider, useTheme } from '@mui/material';
import { Close, Notifications, Dashboard, Settings, VolumeOff, VolumeUp, Assignment, RocketLaunch as RocketLaunchIcon, Storefront as StorefrontIcon, SmartToy } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCatAssistant } from '../contexts/CatAssistantContext';
import { useAuth } from '../contexts/AuthContext';
import LynaChat from './LynaChat';
import '../cat-theme.css';
import LynaAvatar from './LynaAvatar';

// Confetti particle component
const Confetti: React.FC<{ count: number }> = ({ count }) => {
    const particles = Array.from({ length: count });
    const colors = ['#FFD700', '#FF6B9D', '#00D4AA', '#FF6B6B', '#4ECDC4', '#FFE66D', '#FF9A56'];
    const shapes = ['50%', '0%', '20%'];

    return (
        <Box sx={{ position: 'absolute', top: -100, left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none', zIndex: 10000 }}>
            {particles.map((_, i) => (
                <Box
                    key={i}
                    sx={{
                        position: 'absolute',
                        width: Math.random() * 8 + 4,
                        height: Math.random() * 8 + 4,
                        backgroundColor: colors[i % colors.length],
                        borderRadius: shapes[i % shapes.length],
                        animation: `confettiFall${i % 3} ${1.5 + Math.random()}s ease-out forwards`,
                        animationDelay: `${i * 0.05}s`,
                        left: `${(i * 20) - 150}px`,
                        opacity: 0,
                        '@keyframes confettiFall0': {
                            '0%': { transform: 'translateY(0) rotate(0deg) scale(0)', opacity: 1 },
                            '50%': { opacity: 1 },
                            '100%': { transform: `translateY(${150 + Math.random() * 50}px) rotate(720deg) scale(1)`, opacity: 0 }
                        },
                        '@keyframes confettiFall1': {
                            '0%': { transform: 'translateY(0) rotate(0deg) scale(0)', opacity: 1 },
                            '50%': { opacity: 1 },
                            '100%': { transform: `translateY(${130 + Math.random() * 40}px) translateX(-40px) rotate(-540deg) scale(1)`, opacity: 0 }
                        },
                        '@keyframes confettiFall2': {
                            '0%': { transform: 'translateY(0) rotate(0deg) scale(0)', opacity: 1 },
                            '50%': { opacity: 1 },
                            '100%': { transform: `translateY(${160 + Math.random() * 60}px) translateX(40px) rotate(540deg) scale(1)`, opacity: 0 }
                        }
                    }}
                />
            ))}
        </Box>
    );
};

const CatAssistant: React.FC = () => {
    const { message, type, isVisible, isCelebrating, action, hide, say } = useCatAssistant();
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [isHovered, setIsHovered] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isMuted, setIsMuted] = useState(false);
    const [showConfetti, setShowConfetti] = useState(false);
    const [isWalking, setIsWalking] = useState(false);
    const [isChatOpen, setIsChatOpen] = useState(false);
    const theme = useTheme();

    const isDark = theme.palette.mode === 'dark';

    const open = Boolean(anchorEl);
    const isPublic = !user;

    // Occasional walk effect
    useEffect(() => {
        const triggerWalk = () => {
            if (!isCelebrating && !isVisible) {
                setIsWalking(true);
                setTimeout(() => setIsWalking(false), 5000);
            }
        };

        const interval = setInterval(triggerWalk, 20000 + Math.random() * 10000); // Walk every 20-30s
        return () => clearInterval(interval);
    }, [isCelebrating, isVisible]);

    // Public recruitment logic
    useEffect(() => {
        if (isPublic && !isVisible && !isMuted) {
            const timer = setTimeout(() => {
                const recruitmentMessages = [
                    "¡Psst! 🐾 ¿Cansado del caos? Deja que te ayude a organizar tus turnos. ¡Es gratis!",
                    "¡Miau! ✨ Mira lo fácil que es tener tu propia agenda online. ¿Te enseño?",
                    "¡Hola! 👋 Soy Lyna. Puedo encargarme de tus reservas mientras tú tomas una siesta. 😴",
                    "¿Canchas? ¿Cabañas? ¿Peluquería? ¡Puedo con todo! Sumate a Turn.AR 🚀"
                ];
                const randomMsg = recruitmentMessages[Math.floor(Math.random() * recruitmentMessages.length)];
                say(randomMsg, 'tip', 8000, {
                    label: 'Registrarme ahora',
                    onClick: () => navigate('/register')
                });
            }, 10000); // Show after 10s on landing
            return () => clearTimeout(timer);
        }
    }, [isPublic, isVisible, isMuted, say, navigate]);

    // Trigger confetti on celebration
    useEffect(() => {
        if (isCelebrating) {
            setShowConfetti(true);
            const timer = setTimeout(() => setShowConfetti(false), 2500);
            return () => clearTimeout(timer);
        }
    }, [isCelebrating]);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        if (anchorEl) {
            setAnchorEl(null);
        } else {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleNavigate = (path: string) => {
        navigate(path);
        handleClose();
    };

    const toggleMute = () => {
        setIsMuted(!isMuted);
        handleClose();
    };

    // Silence global assistant during public reservation sessions to avoid duplication
    const isPublicReservation = location.pathname.startsWith('/public/board/') || 
                               (!['/dashboard', '/admin', '/tasks', '/clients', '/trash', '/login', '/register'].some(p => location.pathname.startsWith(p)) && 
                                location.pathname !== '/' && location.pathname.split('/').length >= 2);

    return (
        <>
            {!(isPublicReservation && !user) && (
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: 25,
                        right: 25,
                        zIndex: 9999,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        pointerEvents: 'none'
                    }}
                >
                    {/* Confetti Effect */}
                    {showConfetti && <Confetti count={25} />}

                    {/* Speech Bubble */}
                    <Fade in={isVisible && !isMuted} timeout={400}>
                        <Paper
                            elevation={8}
                            sx={{
                                mb: 2,
                                mr: 4,
                                p: 2.5,
                                maxWidth: 320,
                                borderRadius: '28px 28px 6px 28px',
                                background: isCelebrating
                                    ? (isDark ? 'linear-gradient(135deg, rgba(149, 117, 205, 0.2), rgba(30, 30, 45, 0.98))' : 'linear-gradient(135deg, rgba(255, 128, 171, 0.15), rgba(255, 255, 255, 0.98))')
                                    : (isDark ? 'rgba(32, 32, 45, 0.98)' : 'rgba(255, 255, 255, 0.98)'),
                                backdropFilter: 'blur(16px)',
                                border: '2.5px solid',
                                borderColor: isCelebrating
                                    ? (isDark ? '#FFB74D' : '#FF9800')
                                    : (isDark ? 'rgba(149, 117, 205, 0.4)' : 'rgba(255, 128, 171, 0.4)'),
                                boxShadow: isCelebrating
                                    ? (isDark ? '0 12px 40px rgba(255, 183, 77, 0.2)' : '0 12px 40px rgba(255, 152, 0, 0.2)')
                                    : (isDark ? '0 12px 32px rgba(0,0,0,0.7)' : '0 12px 32px rgba(255, 128, 171, 0.15)'),
                                position: 'relative',
                                pointerEvents: 'auto',
                                transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                                '&:hover': {
                                    transform: 'translateY(-4px) scale(1.02)',
                                    boxShadow: isDark ? '0 16px 48px rgba(0,0,0,0.8)' : '0 16px 48px rgba(255, 128, 171, 0.25)',
                                },
                                animation: isCelebrating
                                    ? 'kawaiiBounceBubble 0.6s ease-in-out infinite alternate'
                                    : 'floatBubbleKawaii 4s ease-in-out infinite',
                                '@keyframes floatBubbleKawaii': {
                                    '0%, 100%': { transform: 'translateY(0)' },
                                    '50%': { transform: 'translateY(-8px)' }
                                },
                                '@keyframes kawaiiBounceBubble': {
                                    '0%': { transform: 'translateY(0) scale(1)' },
                                    '100%': { transform: 'translateY(-12px) scale(1.04)' }
                                }
                            }}
                        >
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    hide();
                                }}
                                sx={{
                                    position: 'absolute',
                                    top: 6,
                                    right: 6,
                                    color: 'text.secondary',
                                    opacity: 0.6,
                                    '&:hover': { color: 'error.main', bgcolor: 'rgba(255,0,0,0.1)', opacity: 1 }
                                }}
                            >
                                <Close fontSize="small" sx={{ fontSize: 18 }} />
                            </IconButton>

                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                <Box
                                    sx={{
                                        fontSize: '1.4rem',
                                        mt: 0.5,
                                        opacity: 1,
                                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                                        animation: 'swingEmoji 2s ease-in-out infinite alternate',
                                        '@keyframes swingEmoji': {
                                            '0%': { transform: 'rotate(-10deg) scale(1)' },
                                            '100%': { transform: 'rotate(10deg) scale(1.2)' }
                                        }
                                    }}
                                >
                                    {type === 'alert' ? '🚨' : type === 'tip' ? '💡' : type === 'success' ? '✨' : '💖'}
                                </Box>
                                <Box>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: isDark ? '#EEE' : '#2D3436',
                                            fontWeight: isCelebrating ? 700 : 600,
                                            fontSize: '0.95rem',
                                            lineHeight: 1.6,
                                            fontFamily: '"Outfit", "Quicksand", sans-serif'
                                        }}
                                    >
                                        {message}
                                    </Typography>
                                    {action && (
                                        <Box
                                            onClick={action.onClick}
                                            sx={{
                                                mt: 2,
                                                py: 0.7,
                                                px: 2,
                                                bgcolor: isDark ? '#9575CD' : '#FF80AB',
                                                borderRadius: '14px',
                                                cursor: 'pointer',
                                                fontSize: '0.85rem',
                                                fontWeight: 700,
                                                color: '#FFF',
                                                display: 'inline-block',
                                                transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                                                boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.4)' : '0 4px 12px rgba(255, 128, 171, 0.3)',
                                                '&:hover': {
                                                    transform: 'scale(1.05) translateX(4px)',
                                                    filter: 'brightness(1.1)',
                                                    boxShadow: isDark ? '0 6px 16px rgba(0,0,0,0.5)' : '0 6px 16px rgba(255, 128, 171, 0.4)',
                                                }
                                            }}
                                        >
                                            {action.label} →
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Paper>
                    </Fade>

                    {/* Cat Avatar Container */}
                    <Zoom in={true} style={{ transitionDelay: '100ms' }}>
                        <Box
                            onMouseEnter={() => setIsHovered(true)}
                            onMouseLeave={() => setIsHovered(false)}
                            onClick={handleClick}
                            sx={{
                                width: isCelebrating ? 110 : 95,
                                height: isCelebrating ? 110 : 95,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                pointerEvents: 'auto',
                                position: 'relative',
                                filter: `drop-shadow(0 8px 16px ${isDark ? 'rgba(0,0,0,0.6)' : 'rgba(255, 128, 171, 0.4)'})`,
                                transition: 'all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                                transform: isCelebrating
                                    ? 'scale(1.2) translateY(-5px)'
                                    : isWalking ? `translateX(${Math.sin(Date.now() / 200) * 5}px) translateY(${Math.abs(Math.sin(Date.now() / 150)) * -3}px)` : isHovered || open ? 'scale(1.18) rotate(5deg) translateY(-8px)' : 'scale(1)',
                                '&:active': { transform: 'scale(0.9) translateY(2px)' }
                            }}
                        >
                            <LynaAvatar
                                size="100%"
                                isCelebrating={isCelebrating}
                                isHovered={isHovered}
                                isMuted={isMuted}
                                isWalking={isWalking}
                            />

                            {/* Notification badge if not visible but potentially important */}
                            {!isVisible && !isMuted && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 5,
                                        right: 5,
                                        width: 15,
                                        height: 15,
                                        bgcolor: '#FF5252',
                                        borderRadius: '50%',
                                        border: `3px solid ${isDark ? '#1E1E2D' : '#FFF'}`,
                                        animation: 'pulseBadgeKawaii 1.5s infinite',
                                        '@keyframes pulseBadgeKawaii': {
                                            '0%': { transform: 'scale(0.9)', boxShadow: '0 0 0 0 rgba(255, 82, 82, 0.7)' },
                                            '70%': { transform: 'scale(1.1)', boxShadow: '0 0 0 8px rgba(255, 82, 82, 0)' },
                                            '100%': { transform: 'scale(0.9)', boxShadow: '0 0 0 0 rgba(255, 82, 82, 0)' }
                                        }
                                    }}
                                />
                            )}
                        </Box>
                    </Zoom>

                    {/* Quick Actions Menu */}
                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        onClick={handleClose}
                        disableScrollLock
                        PaperProps={{
                            elevation: 0,
                            sx: {
                                overflow: 'visible',
                                filter: `drop-shadow(0px 8px 24px ${isDark ? 'rgba(0,0,0,0.8)' : 'rgba(255, 128, 171, 0.15)'})`,
                                mt: 2,
                                bgcolor: isDark ? 'rgba(30, 30, 45, 0.98)' : 'rgba(255, 255, 255, 0.98)',
                                backdropFilter: 'blur(20px)',
                                borderRadius: '24px',
                                padding: '8px',
                                border: '1px solid',
                                borderColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                                '& .MuiMenuItem-root': {
                                    borderRadius: '16px',
                                    margin: '4px 6px',
                                    padding: '10px 16px',
                                    transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                                    '&:hover': {
                                        bgcolor: isDark ? 'rgba(149, 117, 205, 0.2)' : 'rgba(255, 128, 171, 0.1)',
                                        transform: 'translateX(8px) scale(1.02)',
                                        color: isDark ? '#D1C4E9' : '#F06292',
                                        '& .MuiListItemIcon-root': {
                                            transform: 'scale(1.2) rotate(-10deg)',
                                            color: 'inherit'
                                        }
                                    }
                                },
                                '&:before': {
                                    content: '""',
                                    display: 'block',
                                    position: 'absolute',
                                    bottom: -10,
                                    right: 40,
                                    width: 15,
                                    height: 15,
                                    bgcolor: isDark ? 'rgba(30, 30, 45, 0.98)' : 'rgba(255, 255, 255, 0.98)',
                                    transform: 'translateY(-50%) rotate(45deg)',
                                    zIndex: 0,
                                    borderBottom: '1px solid',
                                    borderRight: '1px solid',
                                    borderColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                                },
                            },
                        }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
                        transformOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        sx={{ pointerEvents: 'auto' }}
                    >
                        {isPublic ? [
                            <MenuItem key="register" onClick={() => handleNavigate('/register')}>
                                <ListItemIcon>
                                    <RocketLaunchIcon fontSize="small" sx={{ color: '#FF6B9D', transition: 'all 0.3s' }} />
                                </ListItemIcon>
                                <ListItemText primaryTypographyProps={{ fontWeight: 700 }}>Empezar ya 🚀</ListItemText>
                            </MenuItem>,
                            <MenuItem key="login" onClick={() => handleNavigate('/login')}>
                                <ListItemIcon>
                                    <StorefrontIcon fontSize="small" sx={{ color: '#9B59B6', transition: 'all 0.3s' }} />
                                </ListItemIcon>
                                <ListItemText primaryTypographyProps={{ fontWeight: 600 }}>Ingresar</ListItemText>
                            </MenuItem>,
                            <MenuItem key="features" onClick={handleClose}>
                                <ListItemIcon>
                                    <Assignment fontSize="small" sx={{ color: '#4ECDC4', transition: 'all 0.3s' }} />
                                </ListItemIcon>
                                <ListItemText primaryTypographyProps={{ fontWeight: 600 }}>Ver Funcionalidades</ListItemText>
                            </MenuItem>
                        ] : [
                            <MenuItem key="tasks" onClick={() => handleNavigate('/tasks')}>
                                <ListItemIcon>
                                    <Assignment fontSize="small" sx={{ color: isDark ? '#9575CD' : '#FF80AB', transition: 'all 0.3s' }} />
                                </ListItemIcon>
                                <ListItemText primaryTypographyProps={{ fontWeight: 600 }}>Ver Tareas</ListItemText>
                            </MenuItem>,
                            <MenuItem key="inbox" onClick={() => handleNavigate('/admin/inbox')}>
                                <ListItemIcon>
                                    <Notifications fontSize="small" sx={{ color: isDark ? '#9575CD' : '#FF80AB', transition: 'all 0.3s' }} />
                                </ListItemIcon>
                                <ListItemText primaryTypographyProps={{ fontWeight: 600 }}>Notificaciones</ListItemText>
                            </MenuItem>,
                            <MenuItem key="chat" onClick={() => setIsChatOpen(true)}>
                                <ListItemIcon>
                                    <SmartToy fontSize="small" sx={{ color: isDark ? '#9575CD' : '#FF80AB', transition: 'all 0.3s' }} />
                                </ListItemIcon>
                                <ListItemText primaryTypographyProps={{ fontWeight: 600 }}>Chatear con Lyna 🐱</ListItemText>
                            </MenuItem>,
                            <MenuItem key="dashboard" onClick={() => handleNavigate('/dashboard')}>
                                <ListItemIcon>
                                    <Dashboard fontSize="small" sx={{ color: isDark ? '#9575CD' : '#FF80AB', transition: 'all 0.3s' }} />
                                </ListItemIcon>
                                <ListItemText primaryTypographyProps={{ fontWeight: 600 }}>Dashboard</ListItemText>
                            </MenuItem>,
                            <MenuItem key="settings" onClick={() => handleNavigate('/admin/settings')}>
                                <ListItemIcon>
                                    <Settings fontSize="small" sx={{ color: isDark ? '#9575CD' : '#FF80AB', transition: 'all 0.3s' }} />
                                </ListItemIcon>
                                <ListItemText primaryTypographyProps={{ fontWeight: 600 }}>Configuración</ListItemText>
                            </MenuItem>
                        ]}
                        <Divider sx={{ my: 1.5, opacity: isDark ? 0.1 : 0.05 }} />
                        <MenuItem onClick={toggleMute} sx={{ opacity: 0.8 }}>
                            <ListItemIcon>
                                {isMuted ? <VolumeOff fontSize="small" /> : <VolumeUp fontSize="small" />}
                            </ListItemIcon>
                            <ListItemText primaryTypographyProps={{ fontSize: '0.9rem' }}>
                                {isMuted ? 'Activar Sonido' : 'Silenciar Miau'}
                            </ListItemText>
                        </MenuItem>
                    </Menu>
                </Box>
            )}
            <LynaChat isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />
        </>
    );
};

export default CatAssistant;
